import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Drawer from '../../components/Drawer';
import Spinner from '../../components/Spinner';
import { fosError, fosFunds, formatMoney, isVersionConflict, newRequestId, parseMoney, type CampaignView, type GoalRow, type ScopeInput } from '../../lib/fosApi';
import { useFos } from './FosShell';
import { EmptyState, ErrorNote, Icon, Section } from './ui';

const slug = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 38) || 'campaign';

/** Create a campaign (with its first campaign year) or add a campaign year to an existing one. */
function CampaignDrawer({ existing, onClose, onSaved }: { existing: CampaignView | null; onClose: () => void; onSaved: (key: string, year: string) => void }) {
  const thisYear = new Date().getFullYear();
  const [name, setName] = useState(existing?.name ?? '');
  const [year, setYear] = useState(String(existing ? Math.max(...existing.periods.map((p) => Number(p.campaignYear)), thisYear - 1) + 1 : thisYear));
  const [start, setStart] = useState(`${year}-01-01`);
  const [end, setEnd] = useState(`${year}-12-31`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const changeYear = (y: string) => { setYear(y); if (/^\d{4}$/.test(y)) { setStart(`${y}-01-01`); setEnd(`${y}-12-31`); } };
  const save = async () => {
    setBusy(true); setError('');
    try {
      const periods = [...(existing?.periods.map(({ campaignYear, startDate, endDate, status }) => ({ campaignYear, startDate, endDate, status })) ?? []), { campaignYear: year, startDate: start, endDate: end, status: 'open' as const }];
      const key = existing?.key ?? slug(name);
      await fosFunds.saveCampaign({ requestId: newRequestId(), expectedVersion: existing?.version ?? 0, campaign: { key, name: (existing?.name ?? name).trim(), primary: existing?.primary ?? true, status: 'active', periods } });
      onSaved(key, year);
    } catch (cause) { const c = fosError(cause); setError(isVersionConflict(c) ? 'This campaign changed. Reload the page and try again.' : c.message); } finally { setBusy(false); }
  };
  return (
    <Drawer title={existing ? `Add a year to ${existing.name}` : 'New campaign'} subtitle="A campaign year can start and end on any dates." onClose={onClose}>
      <div className="fos-form">
        {!existing && <label>Campaign name<input value={name} onChange={(e) => setName(e.target.value)} placeholder="Annual Friends of Scouting campaign" maxLength={80} autoFocus /></label>}
        <label>Campaign year<input value={year} onChange={(e) => changeYear(e.target.value)} inputMode="numeric" maxLength={4} /><small className="fos-hint">A four-digit label, like 2026.</small></label>
        <div className="fos-form-row">
          <label>Starts<input type="date" value={start} onChange={(e) => setStart(e.target.value)} /></label>
          <label>Ends<input type="date" value={end} onChange={(e) => setEnd(e.target.value)} /></label>
        </div>
        <ErrorNote message={error} />
        <div className="drawer-actions"><button className="btn" onClick={onClose}>Cancel</button><button className="btn primary" onClick={save} disabled={busy || !/^\d{4}$/.test(year) || (!existing && !name.trim())}>{busy ? <Spinner /> : existing ? 'Add year' : 'Create campaign'}</button></div>
      </div>
    </Drawer>
  );
}

function GoalLine({ row, campaignKey, campaignYear, onSaved }: { row: GoalRow; campaignKey: string; campaignYear: string; onSaved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const scope: ScopeInput = row.scopeKind === 'council' ? { kind: 'council' } : { kind: 'district', districtId: row.scopeId! };
  const begin = () => { setText(row.amountCents ? String(row.amountCents / 100) : ''); setError(''); setEditing(true); };
  const save = async (amountCents: number | null) => {
    setBusy(true); setError('');
    try { await fosFunds.saveGoal({ requestId: newRequestId(), campaignKey, campaignYear, scope, amountCents, expectedVersion: row.version }); setEditing(false); onSaved(); }
    catch (cause) { const c = fosError(cause); setError(isVersionConflict(c) ? 'This goal changed. Reload the page.' : c.message); } finally { setBusy(false); }
  };
  const parsed = parseMoney(text);
  return (
    <li className="fos-goal-row">
      <div className="fos-goal-name"><Icon name={row.scopeKind === 'council' ? 'target' : 'users'} size={15} /><strong>{row.label}</strong></div>
      {editing ? (
        <div className="fos-goal-edit">
          <label className="fos-inline">$<input value={text} onChange={(e) => setText(e.target.value)} inputMode="decimal" placeholder="25,000" autoFocus onKeyDown={(e) => { if (e.key === 'Enter' && parsed) void save(parsed); }} /></label>
          <button className="btn primary small" disabled={busy || !parsed} onClick={() => parsed && save(parsed)}>{busy ? <Spinner /> : 'Save'}</button>
          <button className="btn small" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
          {row.amountCents !== null && <button className="linkbtn danger" disabled={busy} onClick={() => save(null)}>Remove goal</button>}
        </div>
      ) : (
        <div className="fos-goal-value">
          {row.amountCents !== null ? <strong className="fos-goal-amount">{formatMoney(row.amountCents)}</strong> : <span className="fos-hint">No goal set</span>}
          {row.canEdit && <button className="btn small" onClick={begin}>{row.amountCents !== null ? 'Change' : 'Set goal'}</button>}
        </div>
      )}
      {error && <div className="fos-goal-error"><ErrorNote message={error} /></div>}
    </li>
  );
}

export default function FosGoals() {
  const { context, refresh } = useFos();
  const [campaigns, setCampaigns] = useState<CampaignView[] | null>(null);
  const [key, setKey] = useState('');
  const [year, setYear] = useState('');
  const [rows, setRows] = useState<GoalRow[] | null>(null);
  const [error, setError] = useState('');
  const [drawer, setDrawer] = useState<'new' | 'year' | null>(null);

  const loadCampaigns = useCallback(async (preferKey?: string, preferYear?: string) => {
    try {
      const { campaigns: list } = await fosFunds.campaigns({});
      const active = list.filter((c) => c.status === 'active');
      setCampaigns(active);
      const chosen = active.find((c) => c.key === (preferKey ?? key)) ?? active.find((c) => c.primary) ?? active[0];
      setKey(chosen?.key ?? '');
      const years = chosen ? [...chosen.periods].map((p) => p.campaignYear).sort().reverse() : [];
      setYear(preferYear && years.includes(preferYear) ? preferYear : years.includes(year) ? year : years[0] ?? '');
    } catch (cause) { setError(fosError(cause).message); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { void loadCampaigns(); }, [loadCampaigns]);

  const campaign = useMemo(() => campaigns?.find((c) => c.key === key) ?? null, [campaigns, key]);
  const years = useMemo(() => (campaign ? campaign.periods.map((p) => p.campaignYear).sort().reverse() : []), [campaign]);
  const loadGoals = useCallback(async () => {
    if (!key || !year) { setRows(null); return; }
    setError('');
    try { setRows((await fosFunds.goals({ campaignKey: key, campaignYear: year })).rows); } catch (cause) { setRows(null); setError(fosError(cause).message); }
  }, [key, year]);
  useEffect(() => { void loadGoals(); }, [loadGoals]);

  if (!campaigns) return <div className="fos-loading"><Spinner label="Loading goals" /></div>;
  const canCampaign = context.capabilities.administerCampaigns;

  return (
    <>
      {campaigns.length === 0 ? (
        <Section title="Goals" icon="target">
          <EmptyState icon="target" title="Create a campaign to set goals">
            Goals belong to a campaign year. {canCampaign ? 'Start by creating your first campaign.' : 'A Friends of Scouting administrator needs to create the campaign first.'}
            {canCampaign && <div style={{ marginTop: 12 }}><button className="btn primary" onClick={() => setDrawer('new')}><Icon name="plus" size={14} /> New campaign</button></div>}
          </EmptyState>
        </Section>
      ) : (
        <>
          <div className="fos-toolbar">
            <label className="fos-inline">Campaign
              <select value={key} onChange={(e) => { setKey(e.target.value); setYear(''); const c = campaigns.find((x) => x.key === e.target.value); setYear(c ? c.periods.map((p) => p.campaignYear).sort().reverse()[0] ?? '' : ''); }}>
                {campaigns.map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
              </select>
            </label>
            <label className="fos-inline">Campaign year
              <select value={year} onChange={(e) => setYear(e.target.value)}>{years.map((y) => <option key={y} value={y}>{y}</option>)}</select>
            </label>
            {canCampaign && (
              <span className="fos-count">
                <button className="linkbtn" onClick={() => setDrawer('year')}>Add a campaign year</button>{' · '}
                <button className="linkbtn" onClick={() => setDrawer('new')}>New campaign</button>
              </span>
            )}
          </div>
          <ErrorNote message={error} />
          <Section title={`${campaign?.name ?? ''} · ${year} goals`} icon="target">
            <p className="fos-lede">Goals are the official targets. Progress toward them counts only money the council has confirmed.</p>
            {rows === null ? <Spinner label="Loading" /> : rows.length === 0 ? <p className="fos-hint">You do not have access to any goals here.</p> : (
              <ul className="fos-goal-list">{rows.map((r) => <GoalLine key={`${r.scopeKind}:${r.scopeId}`} row={r} campaignKey={key} campaignYear={year} onSaved={() => { void loadGoals(); }} />)}</ul>
            )}
            <p className="fos-fine"><Link to="/fos">See progress on the dashboard</Link></p>
          </Section>
        </>
      )}
      {drawer && <CampaignDrawer existing={drawer === 'year' ? campaign : null} onClose={() => setDrawer(null)} onSaved={(k, y) => { setDrawer(null); refresh(); void loadCampaigns(k, y); }} />}
    </>
  );
}
